import { uploadMedia } from './api';
import type { Media } from '../types';

type UploadStatusCallback = (status: boolean) => void;
type UploadProgressCallback = (current: number, total: number, uploaded: number, failed: number) => void;

export const createMediaUploadService = (
  onUploadComplete: (media: Media) => void,
  onUploadError?: (error: string, file: File) => void
) => {
  let uploadInProgress = false;
  let statusCallbacks: UploadStatusCallback[] = [];
  let progressCallbacks: UploadProgressCallback[] = [];

  const setUploadStatus = (status: boolean) => {
    uploadInProgress = status;
    statusCallbacks.forEach(callback => callback(status));
  };

  const setUploadProgress = (current: number, total: number, uploaded: number, failed: number) => {
    progressCallbacks.forEach(callback => callback(current, total, uploaded, failed));
  };

  const processFiles = async (files: FileList): Promise<void> => {
    if (uploadInProgress) {
      return;
    }

    setUploadStatus(true);
    const totalFiles = files.length;
    let uploaded = 0;
    let failed = 0;


    for (let currentIndex = 0; currentIndex < totalFiles; ++currentIndex) {
      const file = files.item(currentIndex);
      if (file) {
        setUploadProgress(currentIndex + 1, totalFiles, uploaded, failed);
        try {
          const uploadedMedia = await uploadMedia([file]);
          if (uploadedMedia === null || uploadedMedia?.data?.[0]?.attributes?.error) {
            throw new Error(uploadedMedia?.data?.[0]?.attributes?.error || 'Failed to upload media');
          } else {
            uploaded = uploaded + 1;
            onUploadComplete(uploadedMedia?.data?.[0]?.attributes?.media);
          }
        } catch (error) {
          failed = failed + 1;
          if (onUploadError) {
            onUploadError(error instanceof Error ? error.message : 'Upload failed', file);
          }
        } finally {
          setUploadProgress(currentIndex + 1, totalFiles, uploaded, failed);
        }
      }
    }

    setUploadStatus(false);
    setUploadProgress(0, 0, uploaded, failed);

  };

  const onUploadStatusChange = (callback: UploadStatusCallback) => {
    statusCallbacks.push(callback);
    return () => {
      statusCallbacks = statusCallbacks.filter(cb => cb !== callback);
    };
  };

  const onUploadProgressChange = (callback: UploadProgressCallback) => {
    progressCallbacks.push(callback);
    return () => {
      progressCallbacks = progressCallbacks.filter(cb => cb !== callback);
    };
  };

  const getUploadInProgress = () => uploadInProgress;

  return {
    processFiles,
    onUploadStatusChange,
    onUploadProgressChange,
    getUploadInProgress,
  };
};

export type MediaUploadService = ReturnType<typeof createMediaUploadService>;
